import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  InputAdornment,
  Divider,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InventoryIcon from '@mui/icons-material/Inventory2Outlined';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { VAT_RATES, calcPriceInclVat, calcPriceExclVat, formatPrice, roundMoney } from '../../utils/vat';
import { formatQuantity } from '../../utils';
import DecimalField from '../shared/DecimalField';
import { useUserStore } from '../../store';
import VatModeToggle from '../shared/VatModeToggle';
import VatConverseLine from '../shared/VatConverseLine';

function EditInvoiceLineDialog({ open, handleClose, line, invoiceId, isCreditNote = false, onUpdate }) {
  const { t } = useTranslation();
  const { currentOrganization } = useUserStore();
  const inventoryEnabled = currentOrganization?.inventory_tracking_enabled ?? false;
  const [description, setDescription] = useState('');
  const [rowFreeText, setRowFreeText] = useState('');
  const [productCode, setProductCode] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [unit, setUnit] = useState('kpl');
  const [unitPrice, setUnitPrice] = useState('');
  const [vatRate, setVatRate] = useState(25.5);
  const [discountPercent, setDiscountPercent] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [priceIncludesVat, setPriceIncludesVat] = useState(false);
  const [reduceFromInventory, setReduceFromInventory] = useState(true);

  useEffect(() => {
    if (line) {
      setDescription(line.description || '');
      setRowFreeText(line.row_free_text || '');
      setProductCode(line.product_code || '');
      setQuantity(parseFloat(line.quantity) || 1);
      setUnit(line.unit || 'kpl');
      setVatRate(parseFloat(line.vat_rate) || 25.5);
      setDiscountPercent(line.discount_percent ? parseFloat(line.discount_percent) : '');
      setDeleteConfirm(false);
      setReduceFromInventory(line.reduce_from_inventory ?? true);
      // Set price and toggle based on how it was originally entered
      if (line.is_margin_vat_scheme) {
        // Margin scheme: always load gross customer price
        setPriceIncludesVat(true);
        setUnitPrice(line.unit_price_incl_vat || '');
      } else if (line.price_entered_with_vat && line.unit_price_incl_vat) {
        setPriceIncludesVat(true);
        setUnitPrice(line.unit_price_incl_vat);
      } else {
        setPriceIncludesVat(false);
        setUnitPrice(line.unit_price_excl_vat || '');
      }
    }
  }, [line]);

  const handleSave = async () => {
    setLoading(true);
    setError(null);
    try {
      // Get the entered price value
      const enteredPriceValue = parseFloat(unitPrice) || 0;

      // Mirror backend sign rules so the user learns before the request.
      // Credit notes are exempt from the double-negative rule: crediting a
      // negative-price row legitimately produces a qty<0 x price<0 row.
      if (line.is_margin_vat_scheme && enteredPriceValue < 0) {
        setError(t('invoices.marginNegativePriceError'));
        setLoading(false);
        return;
      }
      if (!isCreditNote && (parseFloat(quantity) || 0) < 0 && enteredPriceValue < 0) {
        setError(t('invoices.doubleNegativeError'));
        setLoading(false);
        return;
      }

      // Calculate VAT-exclusive price for storage
      let priceExclVat = enteredPriceValue;
      let priceInclVat = null;
      const enteredWithVat = priceIncludesVat && enteredPriceValue !== 0;

      if (enteredWithVat) {
        // User entered VAT-inclusive price - store it as source of truth
        priceInclVat = enteredPriceValue;
        priceExclVat = calcPriceExclVat(enteredPriceValue, vatRate);
      }

      let data;
      if (line.is_margin_vat_scheme) {
        // Margin scheme: always send as gross customer price
        data = {
          description,
          row_free_text: rowFreeText,
          product_code: productCode,
          quantity: parseFloat(quantity) || 1,
          unit,
          unit_price_incl_vat: parseFloat(unitPrice) || 0,
          price_entered_with_vat: true,
          vat_rate: vatRate,
          discount_percent: parseFloat(discountPercent) || 0,
        };
      } else {
        data = {
          description,
          row_free_text: rowFreeText,
          product_code: productCode,
          quantity: parseFloat(quantity) || 1,
          unit,
          unit_price_excl_vat: priceExclVat,
          unit_price_incl_vat: enteredWithVat ? priceInclVat : null,
          price_entered_with_vat: enteredWithVat,
          vat_rate: vatRate,
          discount_percent: parseFloat(discountPercent) || 0,
        };
      }

      // Include inventory flag if line has a part
      if (line.part) {
        data.reduce_from_inventory = reduceFromInventory;
      }
      const response = await axios.patch(
        `/api/invoices/${invoiceId}/lines/${line.id}/`,
        data
      );
      // Fetch updated invoice
      const invoiceResponse = await axios.get(`/api/invoices/${invoiceId}/`);
      onUpdate(invoiceResponse.data);
      handleClose();
    } catch (err) {
      console.error('Error updating line:', err);
      // Backend 400 bodies can contain untranslated technical text (DRF field
      // errors, hardcoded English guards) - never surface them raw. The specific
      // sign-rule errors are pre-validated above with translated messages.
      setError(t('invoices.lineSaveFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) {
      setDeleteConfirm(true);
      return;
    }
    setLoading(true);
    try {
      await axios.delete(`/api/invoices/${invoiceId}/lines/${line.id}/`);
      // Fetch updated invoice
      const invoiceResponse = await axios.get(`/api/invoices/${invoiceId}/`);
      onUpdate(invoiceResponse.data);
      handleClose();
    } catch (err) {
      console.error('Error deleting line:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setDeleteConfirm(false);
    handleClose();
  };

  // Calculations - account for VAT toggle and discount
  const enteredPrice = parseFloat(unitPrice || 0);
  const discountMultiplier = 1 - (parseFloat(discountPercent) || 0) / 100;
  // If price includes VAT, convert to excl VAT for calculations; otherwise use as-is
  const effectivePriceExclVat = priceIncludesVat ? calcPriceExclVat(enteredPrice, vatRate) : enteredPrice;
  const effectivePriceInclVat = priceIncludesVat ? enteredPrice : calcPriceInclVat(enteredPrice, vatRate);
  // Apply discount to line totals (roundMoney avoids floating-point display errors)
  const lineTotalExclVat = roundMoney(quantity * effectivePriceExclVat * discountMultiplier);
  const lineTotalInclVat = roundMoney(quantity * effectivePriceInclVat * discountMultiplier);
  const vatAmount = roundMoney(lineTotalInclVat - lineTotalExclVat);

  if (!line) return null;

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: '16px' },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
        }}
      >
        <Typography
          sx={{
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontWeight: 600,
            fontSize: '18px',
          }}
        >
          {t('Edit line')}
        </Typography>
        <IconButton onClick={handleCancel} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {/* Description */}
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 1 }}>
            {t('Description')}
          </Typography>
          <TextField
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('Line description')}
            fullWidth
            size="small"
            multiline
            rows={2}
            sx={{
              '& .MuiOutlinedInput-root': { borderRadius: '8px' },
            }}
          />
        </Box>

        {/* Additional Description */}
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 1 }}>
            {t('Additional description')}
          </Typography>
          <TextField
            value={rowFreeText}
            onChange={(e) => setRowFreeText(e.target.value)}
            placeholder={t('Optional')}
            fullWidth
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': { borderRadius: '8px' },
            }}
          />
        </Box>

        {/* Product Code */}
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 1 }}>
            {t('Product code')}
          </Typography>
          <TextField
            value={productCode}
            onChange={(e) => setProductCode(e.target.value)}
            placeholder={t('Optional')}
            size="small"
            sx={{
              width: '200px',
              '& .MuiOutlinedInput-root': { borderRadius: '8px' },
            }}
          />
        </Box>

        {/* Quantity and Unit */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <Box>
            <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 1 }}>
              {t('Quantity')}
            </Typography>
            <DecimalField
              value={quantity}
              onChange={setQuantity}
              allowNegative
              size="small"
              inputProps={{ 'data-testid': 'invoice-line-quantity' }}
              sx={{
                width: '120px',
                '& .MuiOutlinedInput-root': { borderRadius: '8px' },
              }}
            />
          </Box>
          <Box>
            <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 1 }}>
              {t('Unit')}
            </Typography>
            <TextField
              value={unit}
              onChange={(e) => setUnit(e.target.value)}
              placeholder="kpl"
              size="small"
              sx={{
                width: '100px',
                '& .MuiOutlinedInput-root': { borderRadius: '8px' },
              }}
            />
          </Box>
        </Box>

        {/* Inventory toggle - only shown for lines with a part */}
        {/* A credit note never deducts; its lines carry the part only so the credit can restock. */}
        {inventoryEnabled && !isCreditNote && line.part && line.stock_info && (
          <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <InventoryIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            <FormControlLabel
              control={
                <Checkbox
                  checked={reduceFromInventory}
                  onChange={(e) => setReduceFromInventory(e.target.checked)}
                  size="small"
                />
              }
              label={
                <Typography variant="body2">
                  {t('Reduce from inventory')}
                </Typography>
              }
              sx={{ ml: 0 }}
            />
            {line.stock_info && (
              <Typography variant="caption" color="text.secondary">
                ({t('Free')}: {formatQuantity(line.stock_info.quantity_free)} {t('pcs')})
              </Typography>
            )}
          </Box>
        )}

        {/* Price and VAT Rate */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 1 }}>
              {line.is_margin_vat_scheme ? t('Customer price') : (priceIncludesVat ? t('Unit price (incl. VAT)') : t('Unit price (excl. VAT)'))}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DecimalField
                value={unitPrice}
                onChange={setUnitPrice}
                allowNegative={!line.is_margin_vat_scheme}
                size="small"
                inputProps={{ 'data-testid': 'invoice-line-unit-price' }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">€</InputAdornment>,
                }}
                sx={{
                  width: '150px',
                  '& .MuiOutlinedInput-root': { borderRadius: '8px' },
                }}
              />
              {!line.is_margin_vat_scheme && (
                <VatModeToggle
                  value={priceIncludesVat ? 'incl' : 'excl'}
                  onChange={(e, val) => val && setPriceIncludesVat(val === 'incl')}
                  sx={{ flexShrink: 0 }}
                />
              )}
            </Box>
            {/* Show calculated price (not for margin scheme) */}
            {!line.is_margin_vat_scheme && (
              <VatConverseLine value={unitPrice} mode={priceIncludesVat ? 'incl' : 'excl'} vatRate={vatRate} />
            )}
            {parseFloat(unitPrice) < 0 && (
              <Typography sx={{ fontSize: '12px', color: 'warning.main' }}>
                {t('negativePriceHint')}
              </Typography>
            )}
          </Box>
          {!line.is_margin_vat_scheme && (
            <Box>
              <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 1 }}>
                {t('VAT rate')}
              </Typography>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <Select
                  value={vatRate}
                  onChange={(e) => setVatRate(e.target.value)}
                  sx={{ borderRadius: '8px' }}
                >
                  {VAT_RATES.map((rate) => (
                    <MenuItem key={rate.value} value={rate.value}>
                      {rate.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
          <Box>
            <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 1 }}>
              {t('Discount %')}
            </Typography>
            <DecimalField
              value={discountPercent}
              onChange={setDiscountPercent}
              max={100}
              size="small"
              placeholder="0"
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
              sx={{
                width: '100px',
                '& .MuiOutlinedInput-root': { borderRadius: '8px' },
              }}
            />
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Line Total with VAT breakdown */}
        {line.is_margin_vat_scheme ? (
          /* Simplified view for margin VAT lines */
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                {t('Total')}
              </Typography>
              <Typography sx={{ fontSize: '18px', fontWeight: 600 }}>
                {formatPrice(lineTotalInclVat)} €
              </Typography>
            </Box>
            <Typography sx={{ fontSize: '12px', color: 'text.secondary', fontStyle: 'italic' }}>
              {t('VAT is calculated on profit margin (margin VAT scheme)')}
            </Typography>
          </Box>
        ) : (
          /* Normal VAT breakdown */
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                {t('Subtotal (excl. VAT)')}
              </Typography>
              <Typography sx={{ fontSize: '14px' }}>
                {formatPrice(lineTotalExclVat)} €
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                {t('VAT')} ({formatPrice(vatRate, 1)} %)
              </Typography>
              <Typography sx={{ fontSize: '14px' }}>
                {formatPrice(vatAmount)} €
              </Typography>
            </Box>
            <Divider sx={{ my: 0.5 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                {t('Total (incl. VAT)')}
              </Typography>
              <Typography sx={{ fontSize: '18px', fontWeight: 600 }}>
                {formatPrice(lineTotalInclVat)} €
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'space-between' }}>
        <Button
          color="error"
          startIcon={<DeleteOutlineIcon />}
          onClick={handleDelete}
          disabled={loading}
          sx={{
            borderRadius: '8px',
            fontFamily: "'Plus Jakarta Sans', sans-serif",
          }}
        >
          {deleteConfirm ? <span>{t('Confirm delete')}</span> : <span>{t('Delete')}</span>}
        </Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            onClick={handleCancel}
            sx={{
              borderRadius: '8px',
              px: 2.5,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
            }}
          >
            {t('Cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={loading || !description.trim()}
            sx={{
              bgcolor: 'secondary.main',
              borderRadius: '8px',
              px: 2.5,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              '&:hover': { bgcolor: 'secondary.dark' },
            }}
          >
            {t('Save')}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

export default EditInvoiceLineDialog;
